import React from 'react';

import {AnalysisGraphQL, AnalysisViewGraphQL, isAnalysisInputStepType} from 'analysis-tool/AnalysisTypes';
import AddTransformStepDropdown from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import {BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE} from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import {FIX_FAILED_TRANSFORMS_MESSAGE} from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import {getTransformPills} from 'analysis-tool/ui/charting/TransformPillUtils';
import AdditionalEditorSection from 'analysis-tool/ui/components/AdditionalEditorSection';
import {PipelineStepDataKind, PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {getLegacyStepFromAnalysis} from 'pipelines/StepUtil';

interface EditViewTransformsProps {
  analysis: AnalysisGraphQL;
  view: AnalysisViewGraphQL;
  dataFrameSpec: PipelineStepRawData<PipelineStepDataKind.DATASET>;
}

export default function EditViewTransforms({
  analysis,
  view,
  dataFrameSpec,
}: EditViewTransformsProps): JSX.Element {
  const viewSteps = view.lineage.map(({step}) => getLegacyStepFromAnalysis(analysis, step));
  const allStepsSucceeded = viewSteps.every(
    (step, index) => step.statusCode === PipelineStepStatusCode.SUCCEEDED || index === viewSteps.length - 1
  );

  const blockedByDownstream = hasDownstreamFlagRowsStep(analysis, viewSteps[viewSteps.length - 1]);
  const isTopLevelView = isAnalysisInputStepType(viewSteps[0].definition.stepType);
  const canAddNewSteps = allStepsSucceeded && !blockedByDownstream && !isTopLevelView;
  const addNewStepsDisabledMessage =
    (!allStepsSucceeded ? FIX_FAILED_TRANSFORMS_MESSAGE : undefined) ??
    (blockedByDownstream ? BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE : undefined);

  return (
    <AdditionalEditorSection
      title="Filters & Transforms"
      addItemElement={
        isTopLevelView ? (
          <></>
        ) : (
          <AddTransformStepDropdown
            viewSteps={viewSteps}
            dataFrameSpec={dataFrameSpec}
            inputDataFrameId={dataFrameSpec.id}
            analysis={analysis}
            canAddNewSteps={canAddNewSteps}
            addNewStepsDisabledMessage={addNewStepsDisabledMessage}
          />
        )
      }
      pills={getTransformPills(analysis, viewSteps)}
      showTopBorder={false}
    />
  );
}
